import { FC } from 'react';
import PageLayout from '@/components/ui/PageLayout/PageLayout.tsx';
import { useTranslation } from 'react-i18next';
import CheckboxInput from '@/components/functional/CheckboxInput/CheckboxInput.tsx';
import LoadingButton from '@/components/ui/LoadingButton/LoadingButton.tsx';
import useUpdateProfile from '@/api/auth/useUpdateProfile.ts';
import type { Consent as ConsentT } from '@/_constants/Consent.ts';
import Consent from '@/_constants/Consent.ts';
import { Alert } from 'react-daisyui';
import { useAuthContext } from '@/context/AuthContext.tsx';
import { useOptimisticState } from '@/hooks/useOptimisticState.ts';
import useDeleteAccount from '@/api/auth/useDeleteAccount.ts';
import useExportUserData from '@/api/auth/useExportUserData.ts';

type ConsentsPageProps = {};

const ConsentsPage: FC<ConsentsPageProps> = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const [storageConsent, setStorageConsent] = useOptimisticState(!!user?.consents?.storage);
  const [analyticsConsent, setAnalyticsConsent] = useOptimisticState(!!user?.consents?.analytics);
  const [marketingConsent, setMarketingConsent] = useOptimisticState(!!user?.consents?.marketing);
  const {
    mutateAsync: updateProfile,
    isPending: isLoadingUpdateProfile,
    error: updateProfileError,
  } = useUpdateProfile();

  const {
    mutateAsync: deleteAccount,
    isPending: isLoadingDeleteAccount,
    error: errorDeleteAccount,
    isSuccess: isSuccessDeleteAccount,
  } = useDeleteAccount();

  const { mutateAsync: exportData, isPending: isLoadingExportData, error: errorExportData } = useExportUserData();

  const handleUpdateConsent = ({ consent, value }: { consent: ConsentT; value: boolean }) => {
    (async () => {
      try {
        const actionByConsent: Record<ConsentT, (value: boolean, fn: () => Promise<void>) => void> = {
          [Consent.STORAGE]: setStorageConsent,
          [Consent.ANALYTICS]: setAnalyticsConsent,
          [Consent.MARKETING]: setMarketingConsent,
        };

        if (!actionByConsent[consent]) return;

        actionByConsent[consent](value, async () => {
          await updateProfile({
            consents: {
              [consent]: value,
            },
          });
        });
      } catch (e) {
        console.error('Failed to update consents:', e);
      }
    })();
  };

  const handleDeleteAccount = () => {
    void (async () => {
      try {
        if (isLoadingDeleteAccount) return;
        await deleteAccount();
      } catch (error) {
        console.error(error);
      }
    })();
  };

  const handleExportData = () => {
    void (async () => {
      try {
        if (isLoadingExportData) return;
        await exportData();
      } catch (error) {
        console.error(error);
      }
    })();
  };

  return (
    <PageLayout name={t('consentsPage.title')}>
      {updateProfileError ? <Alert status="error">{updateProfileError.message}</Alert> : null}
      {errorDeleteAccount ? <Alert status="error">{errorDeleteAccount.message}</Alert> : null}
      {errorExportData ? <Alert status="error">{errorExportData.message}</Alert> : null}
      {isSuccessDeleteAccount ? <Alert status="success">{t('consentsPage.deleteRequestSent')}</Alert> : null}
      <div className="flex flex-col gap-4">
        <p>{t('consentsPage.header')}:</p>
        <div className="flex flex-col items-start">
          <CheckboxInput
            disabled={isLoadingUpdateProfile}
            color="secondary"
            label={t('consentsPage.storage')}
            checked={storageConsent}
            onChange={(e) => handleUpdateConsent({ consent: Consent.STORAGE, value: e.target.checked })}
          />
          <CheckboxInput
            disabled={isLoadingUpdateProfile}
            color="secondary"
            label={t('consentsPage.analytics')}
            checked={analyticsConsent}
            onChange={(e) => handleUpdateConsent({ consent: Consent.ANALYTICS, value: e.target.checked })}
          />
          <CheckboxInput
            disabled={isLoadingUpdateProfile}
            color="secondary"
            label={t('consentsPage.marketing')}
            checked={marketingConsent}
            onChange={(e) => handleUpdateConsent({ consent: Consent.MARKETING, value: e.target.checked })}
          />
        </div>
        <p>{t('consentsPage.export_data_info')}:</p>
        <LoadingButton
          loading={isLoadingExportData}
          color="primary"
          variant="outline"
          className="w-[350px] max-w-full"
          onClick={handleExportData}
        >
          {t('button.export_data')}
        </LoadingButton>
        <p>{t('consentsPage.requestDelete')}:</p>
        <LoadingButton
          disabled={isSuccessDeleteAccount}
          loading={isLoadingDeleteAccount}
          color="error"
          variant="outline"
          className="w-[350px] max-w-full"
          onClick={handleDeleteAccount}
        >
          {t('button.request_data_delete')}
        </LoadingButton>
      </div>
    </PageLayout>
  );
};

export default ConsentsPage;
